import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  MULTIPLE_CHOICE_TEST_VIEW,
  MultipleChoiceTestActiveQuestion,
  MultipleChoiceTestQuestionsState,
  MultipleChoiceQuestionOption,
} from '~/types/multiple-choice'
import { RootState } from '~/redux/store'

interface MultipleChoiceTestState {
  activeQuestion: MultipleChoiceTestActiveQuestion
  executeGemstoneAnimation: boolean
  questionsState: MultipleChoiceTestQuestionsState | null
  view: MULTIPLE_CHOICE_TEST_VIEW
}

const initialState: MultipleChoiceTestState = {
  activeQuestion: {
    index: 0,
    didAnswer: false,
    didAnswerCorrectly: false,
    selectedOption: null,
  },
  executeGemstoneAnimation: false,
  questionsState: null,
  view: MULTIPLE_CHOICE_TEST_VIEW.QUIZ,
}

function resetQuestion(): Omit<typeof initialState.activeQuestion, 'index'> {
  return {
    didAnswer: false,
    didAnswerCorrectly: false,
    selectedOption: null,
  }
}

const slice = createSlice({
  name: 'multiple-choice-test',
  initialState,
  reducers: {
    checkAnswer: (state, action: PayloadAction<{ activeQuestionId: string; didAnswerCorrectly: boolean }>) => {
      const answer = {
        didAnswer: true,
        didAnswerCorrectly: action.payload.didAnswerCorrectly,
      }

      return {
        ...state,
        activeQuestion: {
          ...state.activeQuestion,
          ...answer,
        },
        questionsState: {
          ...state.questionsState,
          [action.payload.activeQuestionId]: answer,
        },
      }
    },

    executeGemstoneAnimation: (state) => ({
      ...state,
      executeGemstoneAnimation: true,
    }),

    goToNextQuestion: (state) => ({
      ...state,
      activeQuestion: {
        index: state.activeQuestion.index + 1,
        ...resetQuestion(),
      },
    }),

    goToSummary: (state) => ({
      ...state,
      view: MULTIPLE_CHOICE_TEST_VIEW.SUMMARY,
      activeQuestion: {
        ...state.activeQuestion,
        ...resetQuestion(),
      },
    }),

    resetMultipleChoiceTest: () => ({
      ...initialState,
    }),

    setInitialQuestionsState: (state, action: PayloadAction<MultipleChoiceTestQuestionsState>) => ({
      ...state,
      questionsState: action.payload,
    }),

    setSelectedOption: (state, action: PayloadAction<MultipleChoiceQuestionOption>) => ({
      ...state,
      activeQuestion: {
        ...state.activeQuestion,
        selectedOption: action.payload,
      },
    }),
  },
})

export const selectMultipleChoiceTestState = (state: RootState): MultipleChoiceTestState => state.multipleChoiceTest
export const {
  checkAnswer: checkAnswerActionCreator,
  executeGemstoneAnimation: executeGemstoneAnimationActionCreator,
  goToNextQuestion: goToNextQuestionActionCreator,
  goToSummary: goToSummaryActionCreator,
  resetMultipleChoiceTest: resetMultipleChoiceTestActionCreator,
  setInitialQuestionsState: setInitialQuestionsStateActionCreator,
  setSelectedOption: setSelectedOptionActionCreator,
} = slice.actions

export default slice.reducer
